import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import clsx from "clsx";
import { useMemo } from "react";
import {
  Controller,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { useDebounce } from "@/shared/lib/useDebounce";
import type { BaseInputProps } from "@/shared/types";
import { formatAmountByConfig } from "../utils/amountFormat";
import { amountToWords } from "../utils/amountToWords";
import { textFormatter } from "../utils/textFormatter";
import { WrapperInput } from "./WrapperInput";

type CurrencyInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  formatValueOnBlur?: (value: string) => string;
  showAmountInWords?: boolean;
  /** Overrides the country-derived currency code (e.g. "INR", "AED"). */
  currencyCode?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/** Strips everything but digits and a single decimal point (max 2 decimal places). */
const sanitizeNumericInput = (value: string) => {
  const digitsAndDot = value.replace(/[^\d.]/g, "");
  const [wholePart, ...decimalParts] = digitsAndDot.split(".");

  if (!decimalParts.length) return wholePart;

  return `${wholePart}.${decimalParts.join("").slice(0, 2)}`;
};

const formatCurrencyInputValue = (value: string) =>
  value.trim() ? formatAmountByConfig(value, { fallback: "" }) : "";

type CurrencyFieldProps = {
  field: ControllerRenderProps<FieldValues, FieldPath<FieldValues>>;
  fieldState: ControllerFieldState;
  label: string;
  description?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  formatValueOnBlur?: (value: string) => string;
  showAmountInWords?: boolean;
  currencyCode: string;
  inputClasses: {
    borderColor?: string;
    bgColor?: string;
    errorBorderColor?: string;
    textColor?: string;
    placeholderColor?: string;
  };
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function CurrencyField({
  field,
  fieldState,
  label,
  description,
  required,
  className,
  disabled,
  placeholder,
  formatValueOnBlur,
  showAmountInWords,
  currencyCode,
  inputClasses,
  onChange,
}: CurrencyFieldProps) {
  const inputValue = String(field.value ?? "");
  const debouncedValue = useDebounce(inputValue, 300);
  const formattedInputValue = formatValueOnBlur
    ? formatValueOnBlur(inputValue)
    : formatCurrencyInputValue(inputValue);
  const amountInWords = useMemo(() => {
    if (!showAmountInWords) return "";
    return amountToWords(debouncedValue, currencyCode);
  }, [debouncedValue, currencyCode, showAmountInWords]);

  const isDebouncing = inputValue !== debouncedValue;
  return (
    <WrapperInput
      label={label}
      error={fieldState.error?.message}
      description={description}
      required={required}
      className={className}
    >
      <div
        className={clsx(
          "flex h-10 items-stretch overflow-hidden rounded-xl border transition focus-within:ring-2",
          getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
          getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
          "focus-within:border-theme-primary focus-within:ring-theme-primary/10",
          fieldState.error &&
            clsx(
              inputClasses.errorBorderColor || "border-theme-danger-bright",
              "focus-within:ring-theme-danger-bright/10"
            ),
          disabled && "cursor-not-allowed opacity-60"
        )}
      >
        <div
          className={clsx(
            "flex w-[60px] shrink-0 items-center gap-1 border-r px-3 text-[13px] font-medium",
            getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
            getThemeClass(inputClasses.textColor, "text-theme-text")
          )}
        >
          {currencyCode}
        </div>
        <input
          {...field}
          id={field.name}
          data-testid={field.name}
          value={formattedInputValue}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const numericValue = sanitizeNumericInput(e.target.value);

            field.onChange(numericValue);
            onChange?.(e);
          }}
          onBlur={() => {
            field.onBlur();
          }}
          className={clsx(
            "h-full w-full border-none bg-transparent px-4 text-right text-[13px] outline-none disabled:cursor-not-allowed",
            getThemeClass(inputClasses.textColor, "text-theme-text"),
            getThemeClass(inputClasses.placeholderColor, "placeholder:text-theme-text-placeholder")
          )}
        />
      </div>
      <div className=" flex min-h-1 items-center justify-end mr-4" aria-live="polite">
        {isDebouncing ? (
          <span className="flex items-center gap-1" role="status" aria-label="Converting amount">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.1s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.9s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500" />
          </span>
        ) : (
          amountInWords && (
            <p
              key={amountInWords}
              className="text-right text-[10px] text-theme-text-soft animate-reveal-ltr"
            >
              {textFormatter(amountInWords)}
            </p>
          )
        )}
      </div>
    </WrapperInput>
  );
}

/** Amount input with a fixed, country-driven currency code badge on the left, like a real payments UI. */
export function CurrencyInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  formatValueOnBlur,
  currencyCode = LOCALISATION_CONFIG.currencyCode,
  showAmountInWords = true,
  required,
  className,
  onChange,
}: CurrencyInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input") as CurrencyFieldProps["inputClasses"];
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? `${label} is required` : false,
        validate: (value) => {
          if (!value) return true;
          // Values are displayed with locale grouping (for example 10,00,000),
          // but the numeric validation must operate on the unformatted value.
          const numericValue = String(value).replace(/,/g, "");
          return /^\d+(\.\d{1,2})?$/.test(numericValue) || `Enter a valid ${label.toLowerCase()}`;
        },
      }}
      render={({ field, fieldState }) => {
        return (
          <CurrencyField
            field={field}
            fieldState={fieldState}
            label={label}
            description={description}
            required={required}
            className={className}
            disabled={disabled}
            placeholder={placeholder}
            formatValueOnBlur={formatValueOnBlur}
            showAmountInWords={showAmountInWords}
            currencyCode={currencyCode}
            inputClasses={inputClasses}
            onChange={onChange}
          />
        );
      }}
    />
  );
}
